import classNames from "classnames";
import connectToStores from "alt-utils/lib/connectToStores";
import includes from "lodash/includes";
import size from "lodash/size";
import PropTypes from "prop-types";
import React, { Component, createRef } from "react";
import TextareaAutosize from "react-autosize-textarea";

import ChatActions from "../../actions/ChatActions";
import ConfigStore from "../../stores/chat/ConfigStore";
import QuestionStore from "../../stores/chat/QuestionStore";
import SvgIcon from "../common/SvgIcon";
import iconSprite from "../../../../images/img/chat/icons.svg";
import { isMobileDevice, isNodeInRoot } from "../../utils/Utils";

/*
 * HEADS UP!
 *
 * This component implements its own shouldComponentUpdate. So if something it's
 * not working correctly or the component is not updating itself, make sure to
 * check that methdod.
 */

export class MessageForm extends Component {
    constructor() {
        super();

        this.state = {
            messageText: "",
            messageTextIsFocused: false,
            restrictedTooltipIsShown: false,
            shouldDisplayAttachmentErrorMessage: false,
        };
        this.onAttachmentErrorMessageClick = this.onAttachmentErrorMessageClick.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onInput = this.onInput.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onKeyPress = this.onKeyPress.bind(this);
        this.onMouseEnter = this.onMouseEnter.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchOutsideOfRestrictedTooltip = this.onTouchOutsideOfRestrictedTooltip.bind(this);
        this.onUploadAttachment = this.onUploadAttachment.bind(this);
        this.onUploadAttachmentClick = this.onUploadAttachmentClick.bind(this);
        this.onUploadAttachmentKeyPress = this.onUploadAttachmentKeyPress.bind(this);

        this.inputRef = createRef();
        this.restrictedTooltipRef = createRef();
    }

    componentDidMount() {
        if (!isMobileDevice()) this.onFocus();
        this.props.setOffsetBottomOfMessageList();
        document.addEventListener("touchstart", this.onTouchOutsideOfRestrictedTooltip);
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.response.id !== nextProps.response.id) {
            this.setState({ messageText: "" });
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const restrictedTooltipIsShownChanged =
            this.state.restrictedTooltipIsShown !== nextState.restrictedTooltipIsShown;
        const messageTextChanged = this.state.messageText !== nextState.messageText;
        const questionIsInteractionAllowedChanged =
            this.props.question.isInteractionAllowed !== nextProps.question.isInteractionAllowed;
        const messageTextIsFocused = this.state.messageTextIsFocused !== nextState.messageTextIsFocused;
        const shouldDisplayAttachmentErrorMessageChanged =
            this.state.shouldDisplayAttachmentErrorMessage !== nextState.shouldDisplayAttachmentErrorMessage;

        return (
            restrictedTooltipIsShownChanged ||
            messageTextIsFocused ||
            messageTextChanged ||
            questionIsInteractionAllowedChanged ||
            shouldDisplayAttachmentErrorMessageChanged ||
            this.props.hasContactInfoPrompt !== nextProps.hasContactInfoPrompt
        );
    }

    componentDidUpdate() {
        this.props.onMessageTextChange(this.state.messageText);
    }

    componentWillUnmount() {
        document.removeEventListener("touchstart", this.onTouchOutsideOfRestrictedTooltip);
    }

    static getStores() {
        return [ConfigStore, QuestionStore];
    }

    static getPropsFromStores() {
        return {
            configId: ConfigStore.getConfig().id,
            hasContactInfoPrompt: QuestionStore.hasContactInfoPrompt(),
        };
    }

    onBlur() {
        this.setState({ messageTextIsFocused: false });
    }

    onInput(event) {
        this.setState({ messageText: event.target.value });
    }

    onFocus() {
        if (this.inputRef.current) {
            this.setState(
                () => ({ messageTextIsFocused: true }),
                () => this.inputRef.current.focus()
            );
        }
    }

    onKeyPress(event) {
        if (!isMobileDevice() && event.keyCode === 13 && !(event.altKey || event.ctrlKey || event.shiftKey)) {
            this.onSubmit(event);
        }
    }

    onSubmit(event) {
        const { question, response, scrollMessageListToBottom } = this.props;
        const { messageText } = this.state;

        if (messageText.trim().length) {
            ChatActions.addNewMessage({
                messageText: messageText.trim(),
                questionUuid: question.uuid,
                responseId: response.id,
            });
            this.setState({ messageText: "" });
            /*
             * Using setTimeout to allow messageList to correctly recompute its
             * height.
             */
            setTimeout(scrollMessageListToBottom, 0);
            if (!isMobileDevice()) this.onFocus();
        } else if (!question.isInteractionAllowed || question.reroutingStatus.restrictedForPoster) {
            this.setState({ restrictedTooltipIsShown: true });
        } else {
            this.onFocus();
        }
        event.preventDefault();
    }

    onMouseEnter() {
        const { isInteractionAllowed } = this.props.question;
        const { restrictedForPoster } = this.props.question.reroutingStatus;

        if (!isInteractionAllowed || restrictedForPoster) {
            this.setState({ restrictedTooltipIsShown: true });
        }
    }

    onMouseLeave() {
        this.setState({ restrictedTooltipIsShown: false });
    }

    onTouchStart() {
        const { isInteractionAllowed } = this.props.question;
        const { restrictedForPoster } = this.props.question.reroutingStatus;

        if (!isInteractionAllowed || restrictedForPoster) {
            this.setState({ restrictedTooltipIsShown: true });
        }
    }

    onTouchOutsideOfRestrictedTooltip(event) {
        const { isInteractionAllowed } = this.props.question;
        const { restrictedForPoster } = this.props.question.reroutingStatus;
        const touchedTooltip = isNodeInRoot(event.target, this.restrictedTooltipRef.current);
        const touchedInput = isNodeInRoot(event.target, this.inputRef.current);

        if (!isInteractionAllowed || restrictedForPoster) {
            if (touchedTooltip || touchedInput) {
                event.preventDefault();
            } else {
                this.setState({ restrictedTooltipIsShown: false });
            }
        }
    }

    onUploadAttachmentClick() {
        this.setState({ shouldDisplayAttachmentErrorMessage: false });
    }

    onUploadAttachmentKeyPress(event) {
        if (event.keyCode === 32 || event.keyCode === 13) {
            this.onUploadAttachmentClick();
        }
    }

    onUploadAttachment(event) {
        const { maxSizeInBytes, supportedContentTypes, supportedFileTypes } = window.companySettings;
        const { configId, question, response } = this.props;
        const file = event.target.files[0];

        if (!file) return;

        const fileExtension = file.name.split(".").pop();
        const isFileExtensionValid = fileExtension && includes(supportedFileTypes, fileExtension.toLowerCase());
        /*
         * we need to split supportedContentTypes because in case of some files (eg. dmg), file.type
         * returns an empty string
         */
        const isFileContentTypeValid = includes(supportedContentTypes.split(/\s*,\s*/), file.type);
        if (file.size >= maxSizeInBytes || !isFileContentTypeValid || !isFileExtensionValid) {
            this.setState({ shouldDisplayAttachmentErrorMessage: true });
        } else {
            ChatActions.uploadAttachment({
                chatConfigId: configId,
                file,
                questionUuid: question.uuid,
                responseId: response.id,
            });
        }
    }

    onAttachmentErrorMessageClick() {
        this.setState({ shouldDisplayAttachmentErrorMessage: false });
    }

    render() {
        const { posterCanUploadAttachments, supportedContentTypes } = window.companySettings;
        const { isInteractionAllowed } = this.props.question;
        const { restrictedForPoster } = this.props.question.reroutingStatus;
        const { restrictedTooltipIsShown, messageTextIsFocused, shouldDisplayAttachmentErrorMessage } = this.state;
        const messageInputIsDisabled = !isInteractionAllowed || restrictedForPoster || this.props.hasContactInfoPrompt;
        const messageInputIsEmpty = size(this.state.messageText.trim()) === 0;
        const contentClass = classNames({
            content: true,
            "is-focused": messageTextIsFocused,
            "is-disabled": messageInputIsDisabled,
        });
        const shouldDisableSubmitButton = messageInputIsEmpty || !isInteractionAllowed;
        const shouldDisplayAttachmentButton = messageInputIsEmpty && isInteractionAllowed && posterCanUploadAttachments;
        const shouldDisplaySubmitButton = (!messageInputIsEmpty || !posterCanUploadAttachments) && isInteractionAllowed;

        let tooltipText = "";
        if (restrictedForPoster) {
            tooltipText = i18n.t("rtm.text.an_agent_will_be_in_touch");
        } else if (!isInteractionAllowed) {
            tooltipText = i18n.t("rtm.text.your_question_is_closed");
        }

        return (
            <form className="message-form-container" onSubmit={this.onSubmit}>
                <div className={contentClass}>
                    <div
                        className="tooltip-wrapper inverted-skin"
                        onMouseEnter={this.onMouseEnter}
                        onMouseLeave={this.onMouseLeave}
                    >
                        <TextareaAutosize
                            className="message-input"
                            disabled={messageInputIsDisabled}
                            onBlur={this.onBlur}
                            onInput={this.onInput}
                            onFocus={this.onFocus}
                            onKeyDown={this.onKeyPress}
                            onResize={this.props.setOffsetBottomOfMessageList}
                            onTouchStart={this.onTouchStart}
                            placeholder={i18n.t("rtm.placeholder.type_your_reply_here")}
                            ref={this.inputRef}
                            rows={1}
                            value={this.state.messageText}
                            tabIndex={0}
                        />
                        {restrictedTooltipIsShown && (
                            <span className="tooltip-content" ref={this.restrictedTooltipRef}>
                                {tooltipText}
                            </span>
                        )}
                    </div>
                    {shouldDisplaySubmitButton && (
                        <button
                            className="btn submit-btn"
                            name="send message"
                            aria-label="send message"
                            disabled={shouldDisableSubmitButton}
                            type="submit"
                        >
                            <SvgIcon src={iconSprite} id="icon-airplane-envelope" />
                        </button>
                    )}
                    {shouldDisplayAttachmentButton && (
                        <div tabIndex={0} className="attachment-btn tooltip-wrapper inverted-skin">
                            <label
                                htmlFor="uploadAttachment"
                                onClick={this.onUploadAttachmentClick}
                                onKeyPress={this.onUploadAttachmentKeyPress}
                                role="button" // eslint-disable-line
                            >
                                <SvgIcon src={iconSprite} id="icon-clippy" />
                            </label>
                            {shouldDisplayAttachmentErrorMessage && (
                                <span
                                    className="tooltip-content"
                                    onClick={this.onAttachmentErrorMessageClick}
                                    dangerouslySetInnerHTML={{ __html: i18n.t("rtm.text.image_attachment_size") }}
                                />
                            )}
                        </div>
                    )}
                    <input
                        accept={supportedContentTypes}
                        className="hidden"
                        id="uploadAttachment"
                        onChange={this.onUploadAttachment}
                        type="file"
                    />
                </div>
            </form>
        );
    }
}

MessageForm.propTypes = {
    configId: PropTypes.string.isRequired,
    hasContactInfoPrompt: PropTypes.bool.isRequired,
    onMessageTextChange: PropTypes.func.isRequired,
    question: PropTypes.shape({
        isInteractionAllowed: PropTypes.bool.isRequired,
        reroutingStatus: PropTypes.shape({
            restrictedForPoster: PropTypes.bool.isRequired,
        }).isRequired,
    }).isRequired,
    response: PropTypes.shape({
        id: PropTypes.number.isRequired,
    }).isRequired,
    scrollMessageListToBottom: PropTypes.func.isRequired,
    setOffsetBottomOfMessageList: PropTypes.func.isRequired,
};

export default connectToStores(MessageForm);
